import { capitalize } from 'lodash'
import type { ReactNode } from 'react'
import { CollapsiblePrompt } from 'v2/shared/components/collapsible-prompt'
import { ChatAttachments } from '../../analysis-v2/chat-attachments'
import { ChosenPath } from '../../components/chosen-path'
import { NodeExecutionDetails } from '../../components/node-execution-details'
import { NodeName } from '../../components/node-name'
import type { NodeExecutedEvent } from '../../types'
import { isTextAgentChannel } from '../../types'
import { NodeExecutionHeader } from '../header'
import { UserMessage } from './user-message'

interface AgentPresetNodeProps {
  node: NodeExecutedEvent
  chunk: NonNullable<NodeExecutedEvent['chunks']>[number]
}

export const AgentPresetNode = ({ node, chunk }: AgentPresetNodeProps) => {
  if (chunk.sender === 'user')
    return (
      <div className="rounded-lg border border-border bg-card p-3">
        <UserMessage text={chunk.text} />
      </div>
    )
  const configMap = new Map<string, ReactNode>()

  // Core node information
  configMap.set(
    'Current node',
    <NodeName name={node.current_node} type={node.current_node_type} key="current-node" />,
  )

  if (node.chosen_path) {
    configMap.set('Chosen path', <ChosenPath node={node} />)
  }

  configMap.set(
    'Previous node',
    <NodeName name={node.previous_node} type={node.previous_node_type} key="previous-node" />,
  )

  // Chunk-specific information
  if (chunk.type) {
    configMap.set('Type', capitalize(chunk.type))
  }

  if (chunk.text) {
    configMap.set('Text', <CollapsiblePrompt text={chunk.text} />)
  }

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <NodeExecutionHeader node={node} />
      <div className="flex flex-col gap-4 border-t border-border px-2 py-3">
        {Array.from(configMap.entries()).map(([key, value]) => {
          if (!value) return null
          return (
            <div key={key} className="flex items-start gap-4">
              <span className="font-inter text-s-400 text-secondary w-28 shrink-0">{key}:</span>
              <span className="font-inter text-s-400 text-primary min-w-0 break-words">
                {value}
              </span>
            </div>
          )
        })}
        {/* A chat preset can also send buttons, a menu or media. Channel-gated:
            WhatsApp interactive presets use the same `attributes` shape, so a
            payload-only check would newly render cards on other channels. Only a
            message chunk carries attributes at all. */}
        {isTextAgentChannel(node.node_config?.channel) &&
        chunk.event_name === 'NODE_CHUNK_CREATED' ? (
          <ChatAttachments attributes={chunk.attributes} />
        ) : null}
      </div>
      <NodeExecutionDetails node={node} />
    </div>
  )
}
